/**
 * WebSocket 전역 초기화 플러그인
 * - 앱 전역에서 하나의 WebSocket 연결 유지
 * - 계좌/토큰 변경 시 private 채널 자동 구독
 * - 잔고/체결 이벤트를 통한 실시간 P/L 반영
 */
import {
  useWebSocketSubscription,
  setWebSocketSubscriptionDependencies,
  LocalStorageService,
  LocalStorageKey,
} from '@template/utils';
import { useGlobalWebSocket } from '@/composables/useGlobalWebSocket';
import { getWebSocketService } from '@/services/websocket';
import { useAccountStore } from '@template/stores';
import type { Plugin } from 'vue';
import { watch } from 'vue';

let installed = false;

const websocketPlugin: Plugin = {
  install() {
    if (installed) return;
    installed = true;

    (async () => {
      try {
        // 전역 WebSocket 연결 초기화
        const { initializeGlobalWebSocket, isConnected } = useGlobalWebSocket();
        await initializeGlobalWebSocket();

        // 계좌 스토어 초기화 확인
        const accountStore = useAccountStore();
        if (!accountStore.isInitialized) {
          const isAccountAuthorized =
            LocalStorageService.getItem(LocalStorageKey.ACCOUNT_TOKEN) !== null;
          await accountStore.initialize(isAccountAuthorized);
        }

        // WebSocket 구독 전역 의존성 설정
        setWebSocketSubscriptionDependencies({
          getWebSocketService,
          getAccessToken: () => LocalStorageService.getItem(LocalStorageKey.ACCESS_TOKEN),
          initializeGlobalWebSocket,
          isConnected,
          getSelectedAccountNo: () => accountStore.selectedAccountNo,
          getAccountToken: () => accountStore.accountTokenComputed,
        });

        // 계좌/토큰 변경 시 자동 구독을 위한 watch 설정
        const { updateAssetWebSocketSubscriptions } = useWebSocketSubscription();

        // 초기 구독 시도
        await updateAssetWebSocketSubscriptions();

        // 계좌/토큰 변경 시 구독 업데이트
        watch(
          [() => accountStore.selectedAccountNo, () => accountStore.accountTokenComputed],
          async () => {
            try {
              await updateAssetWebSocketSubscriptions();
            } catch (error) {
              console.error('[WebSocketPlugin] 구독 업데이트 실패:', error);
            }
          },
          { immediate: false } // 초기 구독은 위에서 이미 처리
        );
      } catch (error) {
        console.error('[WebSocketPlugin] 초기화 실패:', error);
      }
    })();
  },
};

export default websocketPlugin;
